import { ArticleNumberInput } from '@/components/ui/article-number-input';
import { Button } from '@/components/ui/button';
import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { MultiLanguageInput } from '@/components/ui/multi-language-input';
import type { GroupFormData } from '@fame/shared';
import { Trash2 } from 'lucide-react';
import type { Control } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';

interface InvoiceAdjustmentsFieldProps {
  control: Control<GroupFormData>;
}

export function InvoiceAdjustmentsField({ control }: InvoiceAdjustmentsFieldProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invoiceAdjustments',
  });

  return (
    <FormItem>
      <FormLabel>Factuuraanpassingen (groep)</FormLabel>
      <p className="text-xs text-muted-foreground">
        Wordt per deelnemer toegepast: bij split op beide facturen, anders 1 regel x aantal deelnemers.
      </p>
      <div className="space-y-3">
        {fields.length === 0 && <p className="text-sm text-muted-foreground">Geen extra factuurregels ingesteld</p>}
        {fields.map((field, index) => (
          <div key={field.id} className="relative grid gap-3 rounded-md border p-3 md:grid-cols-12">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2"
              onClick={() => remove(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <div className="md:col-span-6 lg:col-span-4">
              <FormField
                control={control}
                name={`invoiceAdjustments.${index}.articleNumber` as const}
                render={({ field }) => (
                  <ArticleNumberInput value={field.value ?? ''} onChange={field.onChange} placeholder="Artikel" />
                )}
              />
            </div>
            <div className="md:col-span-5 lg:col-span-3 pr-8">
              <FormField
                control={control}
                name={`invoiceAdjustments.${index}.amount` as const}
                render={({ field }) => <Input placeholder="Bedrag" {...field} value={field.value ?? ''} />}
              />
            </div>
            <div className="md:col-span-12">
              <FormField
                control={control}
                name={`invoiceAdjustments.${index}.description` as const}
                render={({ field }) => <MultiLanguageInput placeholder="Omschrijving" {...field} />}
              />
            </div>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            append({
              type: 'add_line',
              articleNumber: '',
              description: { nl: '', fr: '', en: '' },
              amount: '',
            })
          }
        >
          Extra regel toevoegen
        </Button>
      </div>
      <FormMessage />
    </FormItem>
  );
}
